import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ShieldCheck, Lock, CreditCard, Zap } from 'lucide-react';
type PlanId = 'standard' | 'elite';
interface Plan {
  id: PlanId;
  emoji: string;
  title: string;
  desc: string;
  priceOld: string;
  priceNew: string;
  url: string;
  badge?: string;
  highlight?: boolean;
}
const plans: Plan[] = [{
  id: 'standard',
  emoji: '🔵',
  title: 'RUGBY PRO KIT',
  desc: '6 tomos + 6 bonos estratégicos',
  priceOld: '$39.990',
  priceNew: '$19.990',
  url: 'https://checkout.activadigital.store/cart/53270448701734:1'
}, {
  id: 'elite',
  emoji: '🔴',
  title: 'RUGBY PRO KIT ÉLITE',
  desc: '6 tomos + 6 bonos + Manual N°9 + Manual N°10',
  priceOld: '$43.990',
  priceNew: '$29.990',
  url: 'https://checkout.activadigital.store/cart/53270450635046:1',
  badge: '⭐ MÁS ELEGIDO',
  highlight: true
}];
export function PricingSelector() {
  // Élite is pre-selected by default (highest price / most popular)
  const [selectedId, setSelectedId] = useState<PlanId>('elite');
  const selected = plans.find(p => p.id === selectedId)!;
  const handleCheckout = () => {
    window.location.href = selected.url;
  };
  return <div className="max-w-3xl mx-auto" data-id="element-289">
      {/* Options stacked, compact */}
      <div className="space-y-3 md:space-y-4 mb-5 md:mb-6" data-id="element-290">
        {plans.map(plan => {
        const isSelected = plan.id === selectedId;
        return <motion.button key={plan.id} type="button" onClick={() => setSelectedId(plan.id)} whileHover={{
          scale: 1.01
        }} whileTap={{
          scale: 0.99
        }} aria-pressed={isSelected} className={`w-full text-left rounded-2xl p-3.5 md:p-5 relative transition-all duration-200 ${isSelected ? plan.highlight ? 'bg-gradient-to-r from-rugby-green-800 to-rugby-green-900 border-2 border-rugby-gold-500 shadow-[0_0_30px_rgba(212,175,55,0.3)]' : 'bg-rugby-green-900 border-2 border-rugby-gold-500 shadow-[0_0_20px_rgba(212,175,55,0.2)]' : 'bg-rugby-green-950 border-2 border-rugby-green-700 hover:border-rugby-green-600 opacity-80 hover:opacity-100'}`} data-id="element-291">
              {/* MÁS ELEGIDO ribbon — only on highlighted plan */}
              {plan.badge && <div className="absolute -top-2.5 left-4 md:left-5 bg-rugby-gold-500 text-rugby-green-950 font-bold text-[10px] md:text-xs px-2.5 py-0.5 rounded-full shadow-md whitespace-nowrap" data-id="element-292">
                  {plan.badge}
                </div>}

              <div className="flex items-center gap-3 md:gap-4" data-id="element-293">
                {/* Radio indicator */}
                <div className={`flex-shrink-0 w-6 h-6 md:w-7 md:h-7 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-rugby-gold-500 bg-rugby-gold-500' : 'border-rugby-green-600 bg-rugby-green-950'}`} data-id="element-294">
                  {isSelected && <Check size={14} className="text-rugby-green-950" strokeWidth={3} data-id="element-295" />}
                </div>

                {/* Title + desc */}
                <div className="flex-1 min-w-0" data-id="element-296">
                  <div className="flex items-center gap-1.5 md:gap-2" data-id="element-297">
                    <span className="text-base md:text-xl" data-id="element-298">{plan.emoji}</span>
                    <h3 className={`font-display text-sm md:text-lg leading-tight tracking-wide ${isSelected && plan.highlight ? 'text-rugby-gold-400 text-glow' : 'text-white'}`} data-id="element-299">
                      {plan.title}
                    </h3>
                  </div>
                  <p className="text-slate-400 text-[11px] md:text-xs mt-0.5 leading-snug" data-id="element-300">
                    {plan.desc}
                  </p>
                </div>

                {/* Price */}
                <div className="flex-shrink-0 text-right" data-id="element-301">
                  <span className="block text-slate-500 line-through text-[11px] md:text-sm leading-none" data-id="element-302">
                    {plan.priceOld}
                  </span>
                  <span className="block font-display text-xl md:text-3xl text-white leading-tight mt-0.5" data-id="element-303">
                    {plan.priceNew}
                  </span>
                </div>
              </div>
            </motion.button>;
      })}
      </div>

      {/* Shared CTA button */}
      <motion.button type="button" onClick={handleCheckout} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} className="group relative w-full bg-gradient-to-b from-rugby-gold-400 to-rugby-gold-600 text-rugby-green-950 font-display text-base md:text-2xl py-4 md:py-5 px-6 rounded-xl shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:shadow-[0_0_50px_rgba(212,175,55,0.6)] transition-shadow duration-300" data-id="element-304">
        <span data-id="element-305">💥 COMPRAR AHORA — {selected.priceNew}</span>
        <div className="absolute inset-0 rounded-xl border-2 border-white/20 group-hover:border-white/40 transition-colors" data-id="element-306" />
      </motion.button>

      {/* Trust strip */}
      <div className="flex flex-wrap justify-center items-center gap-x-4 md:gap-x-6 gap-y-2 mt-4 md:mt-5 text-[11px] md:text-sm text-slate-300" data-id="element-307">
        <span className="flex items-center gap-1.5 font-medium" data-id="element-308">
          <Lock size={13} className="text-rugby-gold-500" data-id="element-309" /> Pago seguro
        </span>
        <span className="flex items-center gap-1.5 font-medium" data-id="element-310">
          <Zap size={13} className="text-rugby-gold-500" data-id="element-311" /> Acceso en 5 min
        </span>
        <span className="flex items-center gap-1.5 font-medium" data-id="element-312">
          <ShieldCheck size={13} className="text-rugby-gold-500" data-id="element-313" /> Garantía 7
          días
        </span>
        <span className="flex items-center gap-1.5 font-medium" data-id="element-314">
          <CreditCard size={13} className="text-rugby-gold-500" data-id="element-315" /> Mercado Pago
        </span>
      </div>
    </div>;
}